import { useEffect } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { zodResolver } from '@hookform/resolvers/zod'

import { ButtonGroup, CardBody } from 'Components/_theme'
import { DepotsSelect } from 'Components/DepotsSelect/DepotsSelect'
import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import { Form } from 'Components/Form/Form'
import { ReplacementDeviceSelect } from 'Components/ReplacementDeviceSelect/ReplacementDeviceSelect'

import {
  E2E_LOST_STOLEN_BUTTON_BACK,
  E2E_LOST_STOLEN_BUTTON_SUBMIT,
  E2E_LOST_STOLEN_DEPOT,
  E2E_LOST_STOLEN_NEXT_DEVICE,
  E2E_LOST_STOLEN_PREVIOUS_DEVICE,
} from 'Constants/e2e'
import { EMPTY_SELECT_OPTION } from 'Constants/form'

import { LOST_STOLEN_MODAL_REPLACEMENT_FORM } from '../../LostOrStolenModal.constants'
import { lostStolenReplacementFormSchema } from '../../LostOrStolenModal.schema'
import {
  LostOrStolenModalReplacementFormProps,
  LostOrStolenModalStepsEnum,
  LostStolenReplacementFormSchema,
} from '../../LostOrStolenModal.types'
import { lostStolenReplacementFormDefaultValues } from '../../LostOrStolenModal.values'

export const LostOrStolenModalReplacementForm = (
  props: LostOrStolenModalReplacementFormProps,
) => {
  const { rental, onSetCurrentStep } = props
  const intl = useIntl()

  const formMethods = useForm<LostStolenReplacementFormSchema>({
    defaultValues: {
      ...lostStolenReplacementFormDefaultValues,
      previousDevice: `${rental.stocklistItem?.deviceDefinition?.name} ${
        rental.stocklistItem?.imei || ''
      }`,
    },
    resolver: zodResolver(lostStolenReplacementFormSchema),
  })

  const { control, setValue } = formMethods
  const watchDepot = useWatch({
    control,
    name: 'depot',
  })

  const handlePrev = () => {
    onSetCurrentStep(LostOrStolenModalStepsEnum.LANDING)
  }

  const handleSubmit = formMethods.handleSubmit(data => {
    onSetCurrentStep(LostOrStolenModalStepsEnum.SUMMARY, undefined, data)
  })

  useEffect(() => {
    // reset replacement device if depot is changed
    setValue('nextDeviceId', EMPTY_SELECT_OPTION)
  }, [setValue, watchDepot?.value])

  return (
    <CardBody>
      <FormProvider {...formMethods}>
        <Form id="LOST_STOLEN_MODAL_REPLACEMENT_FORM" onSubmit={handleSubmit}>
          <Field
            disabled
            e2eSelector={E2E_LOST_STOLEN_PREVIOUS_DEVICE}
            label="Lost_stolen_device"
            name="previousDevice"
            type={FieldTypeEnum.text}
          />

          <DepotsSelect
            containerClassName="mt-4"
            e2eSelector={E2E_LOST_STOLEN_DEPOT}
            label="Depots"
            markAsRequired
            name="depot"
          />

          <ReplacementDeviceSelect
            containerClassName="mt-4"
            depotId={watchDepot?.value ?? ''}
            e2eSelector={E2E_LOST_STOLEN_NEXT_DEVICE}
            label="Replacement_device"
            markAsRequired
            name="nextDeviceId"
            rentalId={rental.id}
          />
        </Form>
      </FormProvider>

      <ButtonGroup
        leftButton={{
          'data-e2e': E2E_LOST_STOLEN_BUTTON_BACK,
          onClick: handlePrev,
          text: intl.formatMessage({ id: 'Back' }),
        }}
        rightButton={{
          'data-e2e': E2E_LOST_STOLEN_BUTTON_SUBMIT,
          form: LOST_STOLEN_MODAL_REPLACEMENT_FORM,
          text: intl.formatMessage({ id: 'Next' }),
          type: 'submit',
        }}
      />
    </CardBody>
  )
}
